import Axios from "axios";
import moment, {Moment} from "moment";
import "moment/locale/en-gb";
import "moment/dist/locale/en-gb";
import "moment/locale/hr";
import "moment/dist/locale/hr";
import "moment/locale/it";
import "moment/dist/locale/it";
import React, {createContext, FC, ReactElement, useCallback, useEffect, useRef, useState,} from "react";
import {useTranslation} from "react-i18next";
import {languageOverrides} from "../locales/overrides";
import {hr} from "../locales/hr";

const tzmoment = require("moment-timezone");
// tzmoment.tz.setDefault("Europe/Zagreb");

const momentDurationFormatSetup = require("moment-duration-format");
momentDurationFormatSetup(moment);

export type availableLanguages = "HR" | "EN" | "IT";

interface ILanguageContext {
    switchLanguage: (language: availableLanguages, database?: string) => void;
    setLanguage: (language: availableLanguages) => void;
    moment: typeof moment;
    serverTimeToReadableTime: (n?: number) => string;
    serverDateToLocalDate: (d?: Date) => Moment;
}

interface ILanguageContextEntry {
    children: () => ReactElement;
}

export const LanguageContext = createContext<ILanguageContext>({} as any);
export const LanguageContextEntry: FC<ILanguageContextEntry> = ({
                                                                    children,
                                                                }) => {
    const {i18n} = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const m = useRef<typeof moment>(moment);

    const setLanguage = useCallback(
        (language: availableLanguages, initial: boolean = false) => {
            switch (language) {
                case "HR":
                    m.current.locale("hr");
                    i18n.changeLanguage("hr").then((r) => {
                    });
                    break;
                case "EN":
                    m.current.locale("en-gb");
                    i18n.changeLanguage("en").then((r) => {
                    });
                    break;
                case "IT":
                    m.current.locale("ita");
                    i18n.changeLanguage("it").then((r) => {
                    });
                    break;
            }

            if (initial) {
                setLoaded(true);
            }
        },
        [],
    );

    useEffect(() => {
        fromStorage();
    }, []);

    const switchLanguage = (language: availableLanguages, database?: string) => {
        setLanguage(language);
        Axios.get("UserInfo/setlanguage?ln=" + language)
            .then(() => {
                localStorage.setItem("language", language);
            })
            .catch();

        if (database) {
            const overrides = languageOverrides[database];
            if (overrides) {
                Object.keys(overrides).forEach((value, index, array) => {
                    const key = value as keyof typeof hr;
                    hr[key] = ((overrides as any)[key] as any)[language as any] as any;
                });

                i18n.reloadResources("hr").then();
            }
        }
    };

    const fromStorage = useCallback(() => {
        const savedLanguage = localStorage.getItem("language") as
            | availableLanguages
            | undefined;
        const toBeSet = savedLanguage ?? "HR";
        setLanguage(toBeSet, true);
    }, [i18n]);

    const serverDateToLocalDate = useCallback(
        (d?: Date) => {
            return m.current
                .utc(d)
                .local();
        },
        [m],
    );
    const serverTimeToReadableTime = useCallback(
        (n?: number) =>
            (m.current.duration(n) as any).format("HH:mm", {trim: false}),
        [m],
    );

    return (
        <LanguageContext.Provider
            value={{
                moment: m.current,
                switchLanguage,
                setLanguage,
                serverTimeToReadableTime,
                serverDateToLocalDate,
            }}
        >
            {loaded ? children() : null}
        </LanguageContext.Provider>
    );
};
